---
title: "vCard QR code for a business card: what to include before printing"
description: "Create a static contact QR for a business card, choose vCard fields, protect private details, export PNG or SVG, and test the print proof."
locale: "en"
date: "2026-07-23"
tags: ["qr", "vcard", "business card", "contacts"]
relatedTools: ["qr-generator"]
translations:
  ru: "qr-kod-vcard-dlya-vizitki"
faq:
  - q: "Can I change the contact after the business card is printed?"
    a: "No. A static vCard QR stores the data directly in its matrix. If the phone, email or website changes, generate a new code and reprint the card."
  - q: "Which vCard fields does qrka support?"
    a: "Name, organization, phone, email and website. Include only work details that you are comfortable sharing with every person who receives the card."
  - q: "Does a static vCard QR include scan statistics?"
    a: "No. The contact details are stored directly in the code, and qrka does not count its scans."
  - q: "Are contact details uploaded during generation?"
    a: "No. The contact is processed in the browser and is not sent to a server. You can download the finished QR as PNG or SVG."
---

A business card has little space, but a recipient may need your name, phone,
email and website without typing them by hand. A vCard QR stores those fields
in the code so a compatible phone can offer to create a contact after scanning.

Open the [QR code generator](/qr-kod?type=vcard), choose vCard, enter only the
necessary fields, export SVG or PNG, and test the code at its real printed size
before ordering a batch. The generator creates a static QR locally in the
browser.

## Which fields to include

Qrka supports name, organization, phone, email and website. That is usually
enough for a professional card.

| Field | When to include it | What to verify |
| --- | --- | --- |
| Name | Almost always | How it should appear in an address book |
| Organization | For a work contact | Current spelling, without a long slogan |
| Phone | If you accept calls | Country code and every digit |
| Email | For written contact | Work address and the domain |
| Website | For a portfolio or company | A complete, working URL |

Do not turn the vCard into a résumé. More embedded text makes the QR matrix
denser and harder to scan at business-card size. Put a title, messenger list,
long bio or collection of links on a website; keep the static contact concise.

## Contact data in a QR is not secret

A static vCard QR stores its fields directly in the image. Anyone with the
card—or a photo of it—can decode the name, phone, email, organization and
website. QR encoding is not encryption.

Use work details that were intended for distribution. A home address, private
number, internal email or any other restricted data does not belong in a
printed code. Wi-Fi has a similar visibility risk but requires a separate
[guest-network workflow](/blog/wifi-qr-code-for-guest-network), not a vCard.

The contact fields are processed in the browser and are not sent to a server.
Printing the result is still a deliberate decision to share those details with
everyone who receives or photographs the card.

## Static or dynamic for a business card

A static vCard works when the contact details are stable and you do not need
scan statistics. It requires no account or subscription. The trade-off is that
you cannot change the details after printing or see how many times the code was
scanned.

The limitation is equally direct: you cannot replace the phone, email or
website after printing. Changed details require a new QR and a new batch. If
you expect frequent changes or need scan counts, compare
[static and dynamic QR codes](/blog/static-or-dynamic-qr-code) before approving
the design. Qrka creates static codes only: their contents cannot be changed
after printing, and scan statistics are unavailable.

## PNG or SVG for the layout

SVG is convenient for a print shop or vector layout because it scales without
blur. PNG is useful when the editor or printer accepts only raster images; in
that case, export a sufficiently large code and do not stretch a tiny PNG after
placing it.

With either format:

- preserve clear space around the matrix;
- use dark modules on a light background;
- do not crop edges or cover the code with text or a logo;
- keep it away from folds, perforations and highly reflective areas;
- test the completed card layout, not only the generator preview.

Qrka blocks color combinations below a 4.5:1 contrast ratio, but a good digital
score does not replace a proof. Paper, lamination, physical size and print
quality also affect scanning.

## Pre-print checklist

1. Scan the code with at least two different phones.
2. Check which fields each contacts app offers to save.
3. Call the number and open the email and website from the saved contact.
4. Print one proof at final size and scan it again.
5. Test under ordinary office light, not only from a bright screen.
6. Keep the source contact details with the design so changes are caught before
   the next batch.

Approve only the layout that works at physical size. For a static QR, this is
the last point where a typo can be fixed without reprinting the cards.
